import { createReducer } from '@reduxjs/toolkit';
import {
  RedactionCode,
  RedactionCodesState,
  SaveRedactionCodeSuccessPayload,
  SetEditRedactionCodePayload,
} from '../models';
import { defaultState, namespace } from '../store';
import {
  FETCH_REDACTION_CODES,
  FETCH_REDACTION_CODES_SUCCESS,
  TOGGLE_IS_DRAWER_OPEN,
  SET_IS_DRAWER_OPEN,
  DELETE_REDACTION_CODE_SUCCESS,
  SET_EDIT_REDACTION_CODE,
  CLEAR_SELECTED_EDIT_REDACTION_CODE,
  SAVE_REDACTION_CODE_SUCCESS,
  SET_REDACTION_CODE_DELETED_SUCCESS,
  ON_CHANGE_PAGE,
  ON_CHANGE_PAGE_SIZE,
  FETCH_COUNT_REDACTION_CODES_SUCCESS,
} from '../actions';
import { keyBy } from 'lodash';

const setIsDrawerOpen = (
  state: RedactionCodesState,
  { payload }: { payload: { isOpen: boolean } }
) => ({
  ...state,
  isDrawerOpen: payload.isOpen,
});

const toggleIsDrawerOpen = (state: RedactionCodesState) => ({
  ...state,
  isDrawerOpen: !state.isDrawerOpen,
});

const fetchRedactionCodes = (state: RedactionCodesState) => ({
  ...state,
  isFetchingCodes: true,
});

const fetchRedactionCodesSuccess = (
  state: RedactionCodesState,
  {
    payload,
  }: {
    payload: {
      structuredDataObjects: {
        records: {
          id: string;
          createdDateTime: string;
          modifiedDateTime: string;
          data: RedactionCode;
        }[];
      };
    };
  }
) => {
  const records = payload.structuredDataObjects.records;
  const redactionCodes: { [key: string]: RedactionCode } = {};

  records.forEach((record) => {
    redactionCodes[record.id] = {
      ...record.data,
      // Stored raw, formatted at render by `settingsDates`. Pre-formatting
      // here with moment produced a fixed en-US string the French build could
      // not localize, and left the table sorting date columns by re-parsing
      // its own display text.
      createdDateTime: record.createdDateTime,
      modifiedDateTime: record.modifiedDateTime,
      id: record.id,
    };
  });

  return {
    ...state,
    redactionCodes,
    isFetchingCodes: false,
  };
};

const deleteRedactionCodeSuccess = (
  state: RedactionCodesState,
  action: {
    payload: {
      deleteStructuredData: {
        id: string;
      };
    };
  }
) => {
  const deletedId = action?.payload?.deleteStructuredData?.id;
  const updatedState = {
    ...state,
    redactionCodes: keyBy(
      Object.values(state.redactionCodes).filter((k) => k.id !== deletedId),
      (v) => v.id
    ),
  };
  return updatedState;
};
const onChangePage = (
  state: RedactionCodesState,
  { payload }: { payload: { offset: number } }
) => {
  const { offset } = payload;
  return {
    ...state,
    offset,
    isFetchingCodes: true,
  };
};

const setEditRedactionCode = (
  state: RedactionCodesState,
  action: { payload: SetEditRedactionCodePayload }
) => ({
  ...state,
  isDrawerOpen: !state.isDrawerOpen,
  selectedRedactionCode: action.payload?.redactionCode,
});

const clearSelectedEditRedactionCode = (state: RedactionCodesState) => ({
  ...state,
  selectedRedactionCode: undefined,
});

const saveRedactionCodeSuccess = (
  state: RedactionCodesState,
  action: {
    payload: SaveRedactionCodeSuccessPayload;
  }
) => {
  const { createStructuredData } = action.payload;
  const { id, data, createdDateTime, modifiedDateTime } = createStructuredData;
  const redactionCodes = {
    ...state.redactionCodes,
    [id]: {
      ...data,
      createdDateTime,
      modifiedDateTime,
    },
  };
  return {
    ...state,
    redactionCodes,
  };
};

const onChangePageSize = (
  state: RedactionCodesState,
  { payload }: { payload: { limit: number } }
) => {
  const { limit } = payload;
  return {
    ...state,
    limit,
    offset: 0,
    isFetchingCodes: true,
  };
};
const fetchCountRedactionCodesSuccess = (
  state: RedactionCodesState,
  { payload }: { payload: { count: number } }
) => {
  const { count } = payload;
  return {
    ...state,
    countRedactionCodes: count,
  };
};

export const redactionCodesReducer = {
  [namespace]: createReducer(defaultState, (builder) => {
    builder
      .addCase(TOGGLE_IS_DRAWER_OPEN, toggleIsDrawerOpen)
      .addCase(SET_IS_DRAWER_OPEN, setIsDrawerOpen)
      .addCase(FETCH_REDACTION_CODES, fetchRedactionCodes)
      .addCase(FETCH_REDACTION_CODES_SUCCESS, fetchRedactionCodesSuccess)
      .addCase(DELETE_REDACTION_CODE_SUCCESS, deleteRedactionCodeSuccess)
      .addCase(SET_EDIT_REDACTION_CODE, setEditRedactionCode)
      .addCase(
        CLEAR_SELECTED_EDIT_REDACTION_CODE,
        clearSelectedEditRedactionCode
      )
      .addCase(SAVE_REDACTION_CODE_SUCCESS, saveRedactionCodeSuccess)
      // Same merge: the soft delete and the restore write the whole record
      // back, they just report through their own action so the drawer and
      // the "Saved" snackbar stay out of it.
      .addCase(SET_REDACTION_CODE_DELETED_SUCCESS, saveRedactionCodeSuccess)
      .addCase(ON_CHANGE_PAGE, onChangePage)
      .addCase(ON_CHANGE_PAGE_SIZE, onChangePageSize)
      .addCase(
        FETCH_COUNT_REDACTION_CODES_SUCCESS,
        fetchCountRedactionCodesSuccess
      );
  }),
};
